import styled from 'styled-components';
import React, { useEffect } from 'react';
import { Box, Typography } from '@material-ui/core';
import { DateDropdown, LineChart, palette, SimplePopover } from '@dev/ee-components';
import { useDispatch, useSelector } from 'react-redux';
import { JourneyDailySummary } from '@dev/ee-api/ee-api-models';
import { isEmpty, map, orderBy } from 'lodash-es';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';
import { parse } from 'query-string';
import { convertToLocalTime, safelyDecodeBase64Url } from '@dev/ee-tools';

import dayjs from '@/services/tools/dayjs';
import { useDateFilterData, isDateFilterAllowed } from '@/services/tools/date';
import { LONG_DATE_FORMAT, TIME_RANGE_WEEK } from '@/services/constants';
import { DateDropdownOptions } from '@/services/dictionaries/DateRangeValues';
import { GetAutomationDailySummaryStart, getAutomationStore } from '@/store/automations/actions';
import { GetAutomationDailySummaryParams } from '@/services/ElasticApi';
import { useDateDropdownValue } from '@/services/hooks/useDateDropdownValue';

const LineChartWrapper = styled.div`
    margin-top: 32px;
    position: relative;
`;

const GraphHeader = styled.div`
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 32px;
`;

const PopoversWrapper = styled.div`
    display: flex;
`;

const mapChartsData = (singleAutomationDailySummary: JourneyDailySummary[]): JourneyDailySummary[] => {
    if (isEmpty(singleAutomationDailySummary)) {
        return [];
    }

    return map(singleAutomationDailySummary, (el) => ({
        Date: convertToLocalTime(el.Date, 'DD-MM'),
        Opened: el.Opened,
        Clicked: el.Clicked,
    }));
};

const chartLineConfig = (): Record<string, string>[] => [
    { color: palette.indigo[6], dataKey: 'Opened' },
    { color: palette.teal[4], dataKey: 'Clicked' },
];

type FilterOptions = { value: string; label: string };

const PopoverOptions = (defaultValue): FilterOptions[] => {
    const { emailsList } = useSelector(getAutomationStore);

    if (isEmpty(emailsList)) {
        return [];
    }

    const dropdownList = map(emailsList, (el) => ({
        value: el.Channel,
        label: el.TemplateName,
    }));

    dropdownList.push({ value: '', label: defaultValue });

    return orderBy(dropdownList, ['value', 'label'], ['asc']);
};

const calcFromDate = (timeRange: number, dateFilter?: string): string | null => {
    if (dateFilter === DateDropdownOptions.All) {
        return null;
    }
    return dayjs().subtract(timeRange, 'day').startOf('day').format(LONG_DATE_FORMAT);
};

export const PerformanceGraph = (): JSX.Element => {
    const { t } = useTranslation();
    const dispatch = useDispatch();
    const { name } = useParams<{ name: string; type: string }>();
    const { singleAutomation, loadingSingleAutomation, singleAutomationDailySummary, dailySummaryPagination } =
        useSelector(getAutomationStore);
    const decodedName = safelyDecodeBase64Url(name);
    const { from, to, email, dateFilter } = parse(location.search);

    const dateFilterQueryParam = dateFilter as string;

    const isDateFilterSelected = isDateFilterAllowed(dateFilterQueryParam);

    const fromQueryParam = (from as string) ?? null;
    const fromParam =
        isDateFilterSelected && fromQueryParam ? fromQueryParam : calcFromDate(TIME_RANGE_WEEK, dateFilterQueryParam);

    const toQueryParam = (to as string) ?? null;
    const toParam = isDateFilterSelected && toQueryParam ? toQueryParam : dayjs().format(LONG_DATE_FORMAT);

    const emailParam = (email as string) ?? null;
    const channelName = dailySummaryPagination?.channelName ?? emailParam;

    const params: GetAutomationDailySummaryParams = {
        name: decodedName,
        from: fromParam,
        to: toParam,
        channelName,
        dateFilter: isDateFilterSelected ? dateFilterQueryParam : '',
    };

    useEffect(() => {
        dispatch(GetAutomationDailySummaryStart(params));
        return () => {
            dispatch(GetAutomationDailySummaryStart({ ...params, from: null, to: null, channelName: null }));
        };
    }, []);

    const onItemClickPopoverHandle = (ev, value?: Record<string, string | number>) => {
        dispatch(
            GetAutomationDailySummaryStart({
                ...params,
                channelName: value?.value as string,
            })
        );
    };

    const calendarOnChangeDropdownHandle = (startDate?: string, endDate?: string) => {
        dispatch(
            GetAutomationDailySummaryStart({
                ...params,
                to: endDate,
                from: dayjs(startDate).startOf('day').format(),
                dateFilter: DateDropdownOptions.Calendar,
            })
        );
    };

    const onItemClickDropdownHandle = (ev, value?: Record<string, string | number>) => {
        dispatch(
            GetAutomationDailySummaryStart({
                ...params,
                to: dayjs().format(LONG_DATE_FORMAT),
                from: value?.value === DateDropdownOptions.All ? null : calcFromDate(value?.value as number),
                dateFilter: value?.value?.toString(),
            })
        );
    };

    return (
        <Box paddingTop="16px" paddingBottom="64px">
            <>
                <GraphHeader>
                    <Typography variant="h3">{t('performance')}</Typography>

                    <PopoversWrapper>
                        <SimplePopover
                            options={PopoverOptions(t('allemails'))}
                            popperProps={{ disablePortal: false }}
                            type="noborder"
                            enableCheckbox={false}
                            value={params?.channelName}
                            defaultPlaceholder={t('allemails')}
                            listItemWidth="300px"
                            maxHeight={350}
                            visibleScrollbar
                            onItemClick={onItemClickPopoverHandle}
                        />

                        <DateDropdown
                            options={useDateFilterData()}
                            value={useDateDropdownValue(false, DateDropdownOptions.Last7days)}
                            defaultPlaceholder={t('last7days')}
                            type="input"
                            returnRawData
                            calendarOnChange={calendarOnChangeDropdownHandle}
                            onItemClick={onItemClickDropdownHandle}
                            buttonVariant="tertiary"
                            startDate={from?.toString()}
                            endDate={to?.toString()}
                        />
                    </PopoversWrapper>
                </GraphHeader>

                <LineChartWrapper>
                    <LineChart
                        lineConfig={chartLineConfig()}
                        data={mapChartsData(singleAutomationDailySummary)}
                        isEmpty={!singleAutomation}
                        height={490}
                        xAxisGroupName="Date"
                        showLegend
                        isMobile={false}
                        xMinTickGap={15}
                        fullWidth
                        isLoading={loadingSingleAutomation || !singleAutomation}
                    />
                </LineChartWrapper>
            </>
        </Box>
    );
};
